import { useBrand } from "hooks/useBrand";
import React from "react";
import { useTranslation } from "react-i18next";

import type { ProPlanCardProps } from "./plans.types";

import styles from "./planCard.module.css";

const PRO_FEATURE_KEYS = ["privateTrees", "unlimitedEditors", "keepPrivate", "everythingFree", "support"] as const;

const ProPlanCard = ({
	autoRenewOn,
	isCancelling,
	isCurrent,
	isSubscribing,
	onCancelAutoRenew,
	onSubscribe,
	planDescription,
	planName,
	priceAmount,
	renewalDate,
	taxNote
}: ProPlanCardProps) => {
	const { t } = useTranslation("plansPage");
	const brand = useBrand();

	return (
		<section className={`${styles.card} ${styles.cardPro}`}>
			{isCurrent ? (
				<span className={styles.currentBadge}>{t("currentPlan")}</span>
			) : (
				<span className={styles.recommendedBadge}>{t("recommended")}</span>
			)}

			<h2 className={styles.planName}>{planName}</h2>
			{planDescription && <p className={styles.planDescription}>{planDescription}</p>}

			{priceAmount && (
				<>
					<div className={styles.price}>
						<span className={styles.priceAmount}>{priceAmount}</span>
						<span className={styles.pricePeriod}>{t("perMonth")}</span>
					</div>
					{taxNote && <p className={styles.taxNote}>{taxNote}</p>}
					<p className={styles.billedNote}>{t("billedMonthly")}</p>
				</>
			)}

			<ul className={styles.featureList}>
				{PRO_FEATURE_KEYS.map((key: string) => (
					<li key={key} className={styles.featureItem}>
						<span aria-hidden="true" className={styles.featureMark}>
							✦
						</span>
						<span>{t(`pro.features.${key}`, brand)}</span>
					</li>
				))}
			</ul>

			{isCurrent && (
				<div className={styles.proState}>
					<p className={styles.proStateTitle}>{t("proActiveTitle")}</p>
					{renewalDate && (
						<p className={styles.proStateMeta}>
							{autoRenewOn ? t("renewsOn", { date: renewalDate }) : t("proUntil", { date: renewalDate })}
						</p>
					)}
					{autoRenewOn ? (
						<button
							className={styles.cancelButton}
							disabled={isCancelling}
							onClick={onCancelAutoRenew}
							type="button">
							{isCancelling ? t("cancelling") : t("cancelAutoRenew")}
						</button>
					) : (
						<p className={styles.autoRenewOffNote}>{t("autoRenewOff")}</p>
					)}
				</div>
			)}

			{!isCurrent && priceAmount && (
				<button className={styles.subscribeButton} disabled={isSubscribing} onClick={onSubscribe} type="button">
					{isSubscribing ? t("subscribing") : t("subscribe")}
				</button>
			)}

			{!isCurrent && !priceAmount && <p className={styles.unavailableNote}>{t("unavailable")}</p>}
		</section>
	);
};

export default ProPlanCard;
